import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

import type { AssignmentCompletionRequirement } from "@/lib/assignmentGating";

export interface Assignment {
  id: string;
  module_id: string;
  title: string;
  description: string | null;
  is_published: boolean;
  /** @deprecated Kept in sync with `completion_requirement` via DB trigger. Read `completion_requirement` instead. */
  required: boolean;
  completion_requirement: AssignmentCompletionRequirement;
  created_at: string;
  updated_at: string;
}

export interface AssignmentQuestion {
  id: string;
  assignment_id: string;
  question_text: string;
  question_type: string;
  is_required: boolean;
  sequence_order: number;
  created_at: string;
  options?: AssignmentQuestionOption[];
}

export interface AssignmentQuestionOption {
  id: string;
  question_id: string;
  option_text: string;
  is_correct: boolean;
  sequence_order: number;
  created_at: string;
}

// Status values for assignment submissions
export type AssignmentSubmissionStatus = 
  | 'submitted'   // Awaiting instructor review
  | 'passed'      // Approved by instructor
  | 'failed'      // Rejected, cannot retry
  | 'retry';      // Needs revision and resubmission

export interface AssignmentSubmission {
  id: string;
  assignment_id: string;
  user_id: string;
  submitted_at: string;
  updated_at: string;
  status: AssignmentSubmissionStatus;
  instructor_feedback: string | null;
  instructor_reviewed_at: string | null;
  instructor_reviewed_by: string | null;
}

export interface AssignmentAnswer {
  id: string;
  submission_id: string;
  question_id: string;
  answer_text: string | null;
  file_url: string | null;
  created_at: string;
  updated_at: string;
}

export function useModuleAssignments(moduleId: string | undefined) {
  return useQuery({
    queryKey: ["module_assignments", moduleId],
    queryFn: async () => {
      if (!moduleId) return [];
      const { data, error } = await supabase
        .from("assignments")
        .select("*")
        .eq("module_id", moduleId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as Assignment[];
    },
    enabled: !!moduleId,
  });
}

export function useAssignmentWithQuestions(assignmentId: string | undefined) {
  return useQuery({
    queryKey: ["assignment_with_questions", assignmentId],
    queryFn: async () => {
      if (!assignmentId) return null;
      
      const { data: assignment, error: assignmentError } = await supabase
        .from("assignments")
        .select("*")
        .eq("id", assignmentId)
        .single();
      
      if (assignmentError) throw assignmentError;
      
      const { data: questions, error: questionsError } = await supabase
        .from("assignment_questions")
        .select("*")
        .eq("assignment_id", assignmentId)
        .order("sequence_order", { ascending: true });
      
      if (questionsError) throw questionsError;
      
      // Fetch options for each question
      const questionsWithOptions = await Promise.all(
        (questions || []).map(async (question) => {
          if (["multiple_choice", "multiple_correct", "true_false"].includes(question.question_type)) {
            const { data: options } = await supabase
              .from("assignment_question_options")
              .select("*")
              .eq("question_id", question.id)
              .order("sequence_order", { ascending: true });
            return { ...question, options: options || [] };
          }
          return { ...question, options: [] };
        })
      );
      
      return {
        ...assignment,
        questions: questionsWithOptions as AssignmentQuestion[],
      };
    },
    enabled: !!assignmentId,
  });
}

export function useCreateAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      module_id: string;
      title: string;
      description?: string;
      completion_requirement?: AssignmentCompletionRequirement;
      /** @deprecated Pass `completion_requirement` instead. */
      required?: boolean;
    }) => {
      const { data: result, error } = await supabase
        .from("assignments")
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["module_assignments", variables.module_id] });
      toast.success("Assignment created");
    },
    onError: (error) => {
      toast.error("Failed to create assignment: " + error.message);
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Assignment> & { id: string }) => {
      const { data, error } = await supabase
        .from("assignments")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      if (!data) throw new Error("Update failed - no data returned");
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["module_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["assignment_with_questions"] });
      toast.success("Assignment updated");
    },
    onError: (error) => {
      toast.error("Failed to update assignment: " + error.message);
    },
  });
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("assignments")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["module_assignments"] });
      toast.success("Assignment deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete assignment: " + error.message);
    },
  });
}

export function useAddAssignmentQuestion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { 
      assignment_id: string; 
      question_text: string; 
      question_type: string;
      is_required: boolean;
      sequence_order: number;
    }) => {
      const { error } = await supabase
        .from("assignment_questions")
        .insert(data);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["assignment_with_questions", variables.assignment_id] });
      toast.success("Question added");
    },
    onError: (error) => {
      toast.error("Failed to add question: " + error.message);
    },
  });
}

export function useAddAssignmentQuestionWithOptions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { 
      assignment_id: string; 
      question_text: string; 
      question_type: string;
      is_required: boolean;
      sequence_order: number;
      options?: { text: string; isCorrect: boolean }[];
    }) => {
      const { options, ...questionData } = data;
      
      // Insert the question
      const { data: newQuestion, error: questionError } = await supabase
        .from("assignment_questions")
        .insert(questionData)
        .select()
        .single();
      
      if (questionError) throw questionError;
      
      // Insert options if provided
      if (options && options.length > 0) {
        const optionsToInsert = options.map((opt, index) => ({
          question_id: newQuestion.id,
          option_text: opt.text,
          is_correct: opt.isCorrect,
          sequence_order: index + 1,
        }));
        
        const { error: optionsError } = await supabase
          .from("assignment_question_options")
          .insert(optionsToInsert);
        
        if (optionsError) throw optionsError;
      }
      
      return { assignmentId: data.assignment_id };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["assignment_with_questions", result.assignmentId] });
      toast.success("Question added");
    },
    onError: (error) => {
      toast.error("Failed to add question: " + error.message);
    },
  });
}

export function useDeleteAssignmentQuestion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ questionId, assignmentId }: { questionId: string; assignmentId: string }) => {
      const { error } = await supabase
        .from("assignment_questions")
        .delete()
        .eq("id", questionId);
      if (error) throw error;
      return { assignmentId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["assignment_with_questions", result.assignmentId] });
      toast.success("Question deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete question: " + error.message);
    },
  });
}

// Submission hooks
export function useUserSubmission(assignmentId: string | undefined) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["user_submission", assignmentId, user?.id],
    queryFn: async () => {
      if (!assignmentId || !user?.id) return null;
      
      const { data: submission, error: submissionError } = await supabase
        .from("assignment_submissions")
        .select("*")
        .eq("assignment_id", assignmentId)
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (submissionError) throw submissionError;
      if (!submission) return null;
      
      const { data: answers, error: answersError } = await supabase
        .from("assignment_answers")
        .select("*")
        .eq("submission_id", submission.id);
      
      if (answersError) throw answersError;
      
      return {
        ...submission,
        answers: answers as AssignmentAnswer[],
      };
    },
    enabled: !!assignmentId && !!user?.id,
  });
}

export function useSubmitAssignment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      assignmentId, 
      answers,
      fileUrls = {},
    }: { 
      assignmentId: string; 
      answers: Record<string, string>;
      fileUrls?: Record<string, string>;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      // Check if submission exists
      const { data: existingSubmission } = await supabase
        .from("assignment_submissions")
        .select("id")
        .eq("assignment_id", assignmentId)
        .eq("user_id", user.id)
        .maybeSingle();
      
      let submissionId: string;
      
      if (existingSubmission) {
        // Update existing submission - reset status to 'submitted' for re-review
        submissionId = existingSubmission.id;
        await supabase
          .from("assignment_submissions")
          .update({ 
            updated_at: new Date().toISOString(),
            status: "submitted", // Reset to submitted for instructor review
            submitted_at: new Date().toISOString(), // Update submission time
          })
          .eq("id", submissionId);
      } else {
        // Create new submission
        const { data: newSubmission, error: createError } = await supabase
          .from("assignment_submissions")
          .insert({
            assignment_id: assignmentId,
            user_id: user.id,
          })
          .select()
          .single();
        
        if (createError) throw createError;
        submissionId = newSubmission.id;
      }
      
      // Upsert answers (skip file URL entries that were added with __file suffix)
      for (const [questionId, answerText] of Object.entries(answers)) {
        if (questionId.endsWith("__file")) continue;
        
        const { error: answerError } = await supabase
          .from("assignment_answers")
          .upsert({
            submission_id: submissionId,
            question_id: questionId,
            answer_text: answerText,
            file_url: fileUrls[questionId] || null,
          }, {
            onConflict: "submission_id,question_id",
          });
        
        if (answerError) throw answerError;
      }
      
      return { submissionId, assignmentId };
    },
    onSuccess: (result) => {
      // Invalidate all assignment-related queries to update UI immediately
      queryClient.invalidateQueries({ queryKey: ["user_submission", result.assignmentId] });
      queryClient.invalidateQueries({ queryKey: ["module-requirements"] });
      queryClient.invalidateQueries({ queryKey: ["student_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["needs_attention_count"] });
      toast.success("Assignment submitted for review");
    },
    onError: (error) => {
      toast.error("Failed to save assignment: " + error.message);
    },
  });
}

// Instructor review hooks
export interface SubmissionWithProfile {
  id: string;
  assignment_id: string;
  user_id: string;
  status: string;
  submitted_at: string;
  updated_at: string;
  instructor_feedback: string | null;
  instructor_reviewed_at: string | null;
  instructor_reviewed_by: string | null;
  profiles: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  } | null;
}

export function useAssignmentSubmissions(assignmentId: string | undefined) {
  return useQuery({
    queryKey: ["assignment_submissions", assignmentId],
    queryFn: async (): Promise<SubmissionWithProfile[]> => {
      if (!assignmentId) return [];
      
      // First get submissions
      const { data: submissions, error: submissionsError } = await supabase
        .from("assignment_submissions")
        .select("*")
        .eq("assignment_id", assignmentId)
        .order("submitted_at", { ascending: false });
      
      if (submissionsError) throw submissionsError;
      if (!submissions || submissions.length === 0) return [];
      
      // Get unique user IDs
      const userIds = [...new Set(submissions.map(s => s.user_id))];
      
      // Fetch profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);
      
      if (profilesError) throw profilesError;
      
      // Map profiles to submissions
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      
      return submissions.map(s => ({
        ...s,
        profiles: profileMap.get(s.user_id) || null,
      }));
    },
    enabled: !!assignmentId,
  });
}

export function useSubmissionWithAnswers(submissionId: string | undefined) {
  return useQuery({
    queryKey: ["submission_with_answers", submissionId],
    queryFn: async () => {
      if (!submissionId) return null;
      
      const { data: submission, error: submissionError } = await supabase
        .from("assignment_submissions")
        .select(`
          *,
          assignments (id, title, description)
        `)
        .eq("id", submissionId)
        .single();
      
      if (submissionError) throw submissionError;

      // Fetch student profile separately (no FK from assignment_submissions.user_id to profiles)
      let profile: { id: string; full_name: string | null; avatar_url: string | null } | null = null;
      if (submission?.user_id) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .eq("id", submission.user_id)
          .maybeSingle();
        profile = profileData ?? null;
      }
      
      const { data: answers, error: answersError } = await supabase
        .from("assignment_answers")
        .select(`
          *,
          assignment_questions (id, question_text, question_type, sequence_order)
        `)
        .eq("submission_id", submissionId);
      
      if (answersError) throw answersError;
      
      // Sort by question sequence_order client-side for reliable ordering
      const sortedAnswers = (answers || []).sort((a, b) => {
        const orderA = a.assignment_questions?.sequence_order ?? 0;
        const orderB = b.assignment_questions?.sequence_order ?? 0;
        return orderA - orderB;
      });
      
      return {
        ...submission,
        profiles: profile,
        answers: sortedAnswers,
      };
    },
    enabled: !!submissionId,
  });
}

export function useUpdateSubmissionFeedback() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ submissionId, feedback }: { submissionId: string; feedback: string }) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("assignment_submissions")
        .update({
          instructor_feedback: feedback,
          instructor_reviewed_at: new Date().toISOString(),
          instructor_reviewed_by: user.id,
        })
        .eq("id", submissionId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assignment_submissions"] });
      queryClient.invalidateQueries({ queryKey: ["submission_with_answers"] });
      toast.success("Feedback saved");
    },
    onError: (error) => {
      toast.error("Failed to save feedback: " + error.message);
    },
  });
}

export function useReviewSubmission() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      submissionId, 
      status, 
      feedback 
    }: { 
      submissionId: string; 
      status: AssignmentSubmissionStatus; 
      feedback?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      // First, get the submission details for the notification
      const { data: submissionData, error: fetchError } = await supabase
        .from("assignment_submissions")
        .select(`
          user_id,
          assignments (id, title, module_id)
        `)
        .eq("id", submissionId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update the submission
      const updateData: Record<string, any> = {
        status,
        instructor_reviewed_at: new Date().toISOString(),
        instructor_reviewed_by: user.id,
      };
      
      if (feedback !== undefined) {
        updateData.instructor_feedback = feedback;
      }
      
      const { error: updateError } = await supabase
        .from("assignment_submissions")
        .update(updateData)
        .eq("id", submissionId);
      
      if (updateError) throw updateError;
      
      // Send notification to the student
      const { notifyAssignmentFeedback } = await import("@/lib/notifications");
      await notifyAssignmentFeedback(submissionData.user_id, {
        status,
        assignmentTitle: (submissionData.assignments as any)?.title || "Assignment",
        assignmentId: (submissionData.assignments as any)?.id,
      });
      
      return { submissionId, status };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["assignment_submissions"] });
      queryClient.invalidateQueries({ queryKey: ["submission_with_answers"] });
      queryClient.invalidateQueries({ queryKey: ["student_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["needs_attention_count"] });
      queryClient.invalidateQueries({ queryKey: ["module-requirements"] });
      queryClient.invalidateQueries({ queryKey: ["can-progress-from-module"] });
      
      const statusMessages: Record<AssignmentSubmissionStatus, string> = {
        passed: "Submission approved! Student has been notified.",
        failed: "Submission marked as failed. Student has been notified.",
        retry: "Revision requested. Student has been notified.",
        submitted: "Status updated.",
      };
      
      toast.success(statusMessages[variables.status]);
    },
    onError: (error) => {
      toast.error("Failed to update submission: " + error.message);
    },
  });
}
